import Image from 'next/image'
import React, { useState } from 'react'

import { faCheck } from '@fortawesome/pro-solid-svg-icons/faCheck'
import { faPalette } from '@fortawesome/pro-solid-svg-icons/faPalette'
import { Box, Container, styled, Typography } from '@mui/material'

import { Button } from '@/components/Button/Button'
import { Icon } from '@/components/Icon/Icon'
import { pxToRem } from '@/shared/styles/themes'
import { artStyleShowcaseData } from '@/slices/HomePage/mocks/artStyleShowcase/data'

export const ArtStyleShowcase: React.FC = () => {
  const {
    eyebrow,
    headline,
    headlineAccent,
    subtitle,
    ctaText,
    ctaLink,
    originalImageUrl,
    originalImageAlt,
    originalLabel,
    styleLabel,
    styles,
  } = artStyleShowcaseData

  const [selectedId, setSelectedId] = useState<string>(styles[0]?.id ?? '')
  const selected = styles.find((s) => s.id === selectedId) ?? styles[0]

  return (
    <StyledSection>
      <Container maxWidth="lg">
        <StyledHeader>
          <StyledEyebrow>
            <Icon icon={faPalette} sx={{ fontSize: pxToRem(14), mr: 1 }} />
            {eyebrow}
          </StyledEyebrow>
          <StyledHeadline variant="h2">
            {headline} <StyledAccent>{headlineAccent}</StyledAccent>
          </StyledHeadline>
          <StyledSubtitle variant="body1">{subtitle}</StyledSubtitle>
        </StyledHeader>

        <StyledBeforeAfter>
          <StyledImageSide>
            <StyledImageLabel>{originalLabel}</StyledImageLabel>
            <StyledImageOrb>
              <Image
                src={originalImageUrl}
                alt={originalImageAlt}
                fill
                sizes="(max-width: 768px) 45vw, 260px"
                style={{ objectFit: 'cover' }}
              />
            </StyledImageOrb>
          </StyledImageSide>

          <StyledArrowWrapper>
            <Image
              src="/images/artstyles/arrow.png"
              alt=""
              width={120}
              height={72}
              style={{ objectFit: 'contain', width: '100%', height: 'auto' }}
            />
          </StyledArrowWrapper>

          <StyledImageSide>
            <StyledImageLabel $accent>{selected?.name ?? styleLabel}</StyledImageLabel>
            <StyledImageOrb $highlighted>
              {selected && (
                <Image
                  key={selected.id}
                  src={selected.imageUrl}
                  alt={selected.imageAlt}
                  fill
                  sizes="(max-width: 768px) 45vw, 260px"
                  style={{ objectFit: 'cover' }}
                />
              )}
            </StyledImageOrb>
          </StyledImageSide>
        </StyledBeforeAfter>

        <StyledPickerHeading variant="overline">Try another style</StyledPickerHeading>

        <StyledGrid>
          {styles.map((style) => {
            const isSelected = style.id === selected?.id

            return (
              <StyledStyleCard
                key={style.id}
                type="button"
                onClick={() => setSelectedId(style.id)}
                $selected={isSelected}
                aria-pressed={isSelected}
                aria-label={`Preview ${style.name} art style`}
              >
                <StyledThumbFrame $selected={isSelected}>
                  <Image
                    src={style.imageUrl}
                    alt={style.imageAlt}
                    fill
                    sizes="(max-width: 600px) 40vw, (max-width: 960px) 22vw, 14vw"
                    style={{ objectFit: 'cover' }}
                  />
                  {isSelected && (
                    <StyledCheckBadge>
                      <Icon icon={faCheck} sx={{ fontSize: pxToRem(12) }} />
                    </StyledCheckBadge>
                  )}
                </StyledThumbFrame>
                <StyledStyleName $selected={isSelected}>{style.name}</StyledStyleName>
              </StyledStyleCard>
            )
          })}
        </StyledGrid>

        <StyledCtaRow>
          <StyledCtaButton arrow primary size="large" href={ctaLink}>
            {ctaText}
          </StyledCtaButton>
        </StyledCtaRow>
      </Container>
    </StyledSection>
  )
}

const StyledSection = styled('section')(({ theme }) => ({
  background: 'linear-gradient(135deg, #FFF6EF 0%, #FBEBDB 100%)',
  padding: theme.spacing(5, 0),

  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(8, 0),
  },
}))

const StyledHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(4),

  [theme.breakpoints.up('md')]: {
    marginBottom: theme.spacing(5),
  },
}))

const StyledEyebrow = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  backgroundColor: 'rgba(220, 174, 150, 0.25)',
  color: theme.palette.primary.main,
  padding: theme.spacing(0.75, 1.75),
  borderRadius: pxToRem(999),
  fontSize: pxToRem(13),
  fontWeight: 700,
  letterSpacing: pxToRem(1.2),
  textTransform: 'uppercase',
}))

const StyledHeadline = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontWeight: 700,
  fontSize: pxToRem(28),
  lineHeight: 1.2,
  maxWidth: pxToRem(720),

  [theme.breakpoints.up('sm')]: {
    fontSize: pxToRem(34),
  },

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(40),
  },
}))

const StyledAccent = styled('span')(({ theme }) => ({
  color: theme.palette.primary.main,
  fontStyle: 'italic',
}))

const StyledSubtitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.primary.main,
  opacity: 0.8,
  maxWidth: pxToRem(620),
  fontSize: pxToRem(15),
  lineHeight: 1.55,

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(17),
  },
}))

const StyledBeforeAfter = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: '1fr auto 1fr',
  alignItems: 'center',
  gap: theme.spacing(1),
  width: '100%',
  maxWidth: pxToRem(680),
  margin: '0 auto',

  [theme.breakpoints.up('sm')]: {
    gap: theme.spacing(3),
  },
}))

const StyledImageSide = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(1.5),
}))

const StyledImageLabel = styled('span', {
  shouldForwardProp: (prop) => prop !== '$accent',
})<{ $accent?: boolean }>(({ theme, $accent }) => ({
  fontSize: pxToRem(12),
  fontWeight: 700,
  letterSpacing: pxToRem(1),
  textTransform: 'uppercase',
  color: $accent ? theme.palette.primary.main : theme.palette.text.secondary,
  opacity: $accent ? 1 : 0.6,

  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(13),
  },
}))

const StyledImageOrb = styled('div', {
  shouldForwardProp: (prop) => prop !== '$highlighted',
})<{ $highlighted?: boolean }>(({ theme, $highlighted }) => ({
  position: 'relative',
  width: '100%',
  aspectRatio: '1 / 1',
  borderRadius: '50%',
  overflow: 'hidden',
  backgroundColor: 'transparent',
  boxShadow: $highlighted
    ? '0 20px 45px rgba(108, 60, 20, 0.22)'
    : '0 10px 30px rgba(108, 60, 20, 0.12)',
  transition: 'box-shadow 0.3s ease',
}))

const StyledArrowWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: pxToRem(40),
  opacity: 0.85,

  [theme.breakpoints.up('sm')]: {
    width: pxToRem(80),
  },

  [theme.breakpoints.up('md')]: {
    width: pxToRem(120),
  },
}))

const StyledPickerHeading = styled(Typography)(({ theme }) => ({
  display: 'block',
  marginTop: theme.spacing(4),
  marginBottom: theme.spacing(2),
  textAlign: 'center',
  color: theme.palette.primary.main,
  opacity: 0.7,
  fontWeight: 700,
  letterSpacing: pxToRem(1.5),
}))

const StyledGrid = styled('div')(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(4, 1fr)',
  gap: theme.spacing(1.5),

  [theme.breakpoints.up('sm')]: {
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: theme.spacing(2),
  },
}))

const StyledStyleCard = styled('button', {
  shouldForwardProp: (prop) => prop !== '$selected',
})<{ $selected: boolean }>(({ theme, $selected }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  padding: theme.spacing(0.5),
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  transition: 'transform 0.2s ease',

  '&:hover': {
    transform: 'translateY(-3px)',
  },

  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: pxToRem(4),
    borderRadius: pxToRem(12),
  },

  ...($selected && {
    transform: 'translateY(-3px)',
  }),
}))

const StyledThumbFrame = styled('div', {
  shouldForwardProp: (prop) => prop !== '$selected',
})<{ $selected: boolean }>(({ theme, $selected }) => ({
  position: 'relative',
  width: '100%',
  aspectRatio: '1 / 1',
  borderRadius: '50%',
  overflow: 'hidden',
  backgroundColor: '#F5EDE5',
  boxShadow: $selected
    ? `0 0 0 3px ${theme.palette.primary.main}, 0 8px 20px rgba(108, 60, 20, 0.22)`
    : '0 4px 12px rgba(108, 60, 20, 0.10)',
  transition: 'box-shadow 0.2s ease',
}))

const StyledCheckBadge = styled('div')(({ theme }) => ({
  position: 'absolute',
  top: pxToRem(4),
  right: pxToRem(4),
  width: pxToRem(22),
  height: pxToRem(22),
  borderRadius: '50%',
  backgroundColor: theme.palette.primary.main,
  color: '#FFFFFF',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.2)',
}))

const StyledStyleName = styled(Typography, {
  shouldForwardProp: (prop) => prop !== '$selected',
})<{ $selected: boolean }>(({ theme, $selected }) => ({
  fontSize: pxToRem(12),
  fontWeight: $selected ? 700 : 500,
  color: theme.palette.primary.main,
  textAlign: 'center',
  opacity: $selected ? 1 : 0.75,
  lineHeight: 1.2,
}))

const StyledCtaRow = styled('div')(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginTop: theme.spacing(4),
}))

const StyledCtaButton = styled(Button)(({ theme }) => ({
  color: theme.palette.secondary.main,
  fontWeight: 600,
  padding: theme.spacing(2, 4),
}))
